'use client';

import { ChevronRightIcon } from '@c/icons';
import { cva, VariantProps } from 'class-variance-authority';
import { ChevronDownIcon } from 'lucide-react';
import { usePathname } from 'next/navigation';
import React, { useState } from 'react';

const ProductPolicyAccordionStyles = cva('transition-all cursor-pointer', {
  variants: {
    hover: {
      default: 'hover:text-brand-secondary ',
      text: 'hover:text-brand-secondary',
      'text-green': 'hover:text-brand-mid-green',
      none: '',
    },
    intent: {
      default: 'p-[0.8rem] ',
      faq: 'p-0 pb-[1.6rem]',
      fullWidth: 'p-0 py-[0.8rem]',
    },
  },
  defaultVariants: {
    hover: 'default',
    intent: 'default',
  },
});

interface ProductPolicyAccordionProps
  extends VariantProps<typeof ProductPolicyAccordionStyles> {
  title: string | React.ReactElement;
  children: React.ReactNode;
  startOpen?: boolean;
  highlightHeaderOnOpen?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
  arrowStyles?: string;
  rightSlot?: React.ReactNode;
  locked?: boolean;
  arrowRight?: boolean;
  mobileArrowRotation?: boolean;
  titleClassName?: string;
  className?: string;
  icon?: React.ReactNode;
}

const ProductPolicyAccordion = ({
  title,
  children,
  startOpen,
  highlightHeaderOnOpen,
  onOpen,
  onClose,
  arrowStyles,
  rightSlot,
  locked,
  arrowRight,
  mobileArrowRotation = false,
  titleClassName,
  className,
  icon,
}: ProductPolicyAccordionProps) => {
  const pathname = usePathname();
  const [open, setOpen] = useState(!!startOpen);

  const getIconRotation = () => {
    return open
      ? mobileArrowRotation
        ? 'rotate-180'
        : 'rotate-90'
      : 'rotate-0';
  };

  const getHeaderColor = () => {
    return highlightHeaderOnOpen && open
      ? 'text-brand-secondary'
      : 'text-brand-black';
  };

  function handleClick() {
    if (locked) return;
    if (open) {
      onClose?.();
      setOpen(false);
    } else {
      onOpen?.();
      setOpen(true);
    }
  }

  return (
    <div className="w-full">
      <div
        onClick={handleClick}
        className={`relative flex w-full cursor-pointer items-center justify-between rounded-[1rem] bg-[#F6F6F6] p-4 px-6
        ${getHeaderColor()}
        ${!!arrowStyles ? arrowStyles : ''}
        ${className ?? ''}`}
      >
        <div className="flex items-center gap-x-4">
          {icon}
          <span
            className={`w-full ${titleClassName} text-[1.8rem] font-semibold`}
          >
            {title}
          </span>
        </div>

        {!arrowRight && (
          <div
            className={`${getIconRotation()} ml-4 w-12 transition-transform duration-200 ease-in-out sm:w-10`}
          >
            <ChevronRightIcon className="h-full w-full" />
          </div>
        )}

        {arrowRight && (
          <div
            className={`${getIconRotation()} ml-4 w-12 transition-transform duration-200 ease-in-out sm:w-10`}
          >
            <ChevronDownIcon className="h-full w-full" />
          </div>
        )}
        {rightSlot}
      </div>

      <div
        className={`grid transition-all duration-200 ease-in-out ${
          open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
        }`}
      >
        <div className="overflow-hidden">
          <div className="w-full px-6 py-6">{children}</div>
        </div>
      </div>

      {/* render content for SEO / screen readers */}
      <span className="sr-only">{children}</span>
    </div>
  );
};

export default ProductPolicyAccordion;
